import axios, { type AxiosResponse } from "axios";
import type { Dispatch } from "redux";
import * as actionTypes from "./actionTypes";
import { config } from "../../constants";
import type { ApiErrorPayload } from "../../types/api";
import type { LoginResponse, Tenant } from "../../types/auth";
import { storedTenant } from "../../shared/tenantStorage";

export interface AccountDetailsPayload {
  name: string;
  email: string;
}

export type AuthAction =
  | { type: typeof actionTypes.AUTH_START }
  | {
      type: typeof actionTypes.AUTH_SUCCESS;
      authToken: string;
      name: string;
      email: string;
      id: number;
      userType: number;
      quickbooksConnected: boolean;
      tenant: Tenant | null;
    }
  | { type: typeof actionTypes.AUTH_FAIL; error?: AxiosResponse<ApiErrorPayload> }
  | { type: typeof actionTypes.AUTH_LOGOUT }
  | { type: typeof actionTypes.UPDATE_ACCOUNT_SUCCESS; name: string; email: string }
  | { type: typeof actionTypes.UPDATE_TENANT_SUCCESS; tenant: Tenant };

export const authStart = (): AuthAction => ({ type: actionTypes.AUTH_START });

export const authSuccess = (authData: LoginResponse): AuthAction => {
  const { user, token } = authData;
  localStorage.setItem("authToken", token);
  localStorage.setItem("first_name", user.first_name);
  localStorage.setItem("last_name", user.last_name);
  localStorage.setItem("email", user.email);
  localStorage.setItem("id", String(user.id));
  localStorage.setItem("userType", String(user.userType));
  localStorage.setItem("quickbooksConnected", String(user.quickbooks_connected));
  localStorage.setItem("tenant", JSON.stringify(user.tenant));
  localStorage.removeItem("business");

  return {
    type: actionTypes.AUTH_SUCCESS,
    authToken: token,
    name: `${user.first_name} ${user.last_name}`.trim(),
    email: user.email,
    id: user.id,
    userType: user.userType,
    quickbooksConnected: user.quickbooks_connected,
    tenant: user.tenant,
  };
};

export const updateAccountSuccess = (user: AccountDetailsPayload): AuthAction => {
  localStorage.setItem("first_name", user.name);
  localStorage.setItem("last_name", "");
  localStorage.setItem("email", user.email);
  return { type: actionTypes.UPDATE_ACCOUNT_SUCCESS, name: user.name, email: user.email };
};

export const updateTenantSuccess = (tenant: Tenant): AuthAction => {
  localStorage.setItem("tenant", JSON.stringify(tenant));
  localStorage.removeItem("business");
  return { type: actionTypes.UPDATE_TENANT_SUCCESS, tenant };
};

export const authFail = (error?: AxiosResponse<ApiErrorPayload>): AuthAction => ({
  type: actionTypes.AUTH_FAIL,
  error,
});

export const logout = (): AuthAction => {
  [
    "authToken",
    "first_name",
    "last_name",
    "email",
    "id",
    "userType",
    "quickbooksConnected",
    "tenant",
    "business",
    "cart",
    "orderDetails",
  ].forEach((key) => localStorage.removeItem(key));
  return { type: actionTypes.AUTH_LOGOUT };
};

export const auth =
  (email: string, password: string) =>
  async (dispatch: Dispatch<AuthAction>): Promise<void> => {
    dispatch(authStart());
    try {
      const response = await axios.post<LoginResponse>(`${config.url.API_URL}login`, {
        email,
        password,
      });
      dispatch(authSuccess(response.data));
    } catch (error: unknown) {
      dispatch(authFail(axios.isAxiosError<ApiErrorPayload>(error) ? error.response : undefined));
    }
  };

export const authCheckState =
  () =>
  (dispatch: Dispatch<AuthAction>): void => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      dispatch(logout());
      return;
    }
    dispatch(
      authSuccess({
        token,
        user: {
          first_name: localStorage.getItem("first_name") ?? "",
          last_name: localStorage.getItem("last_name") ?? "",
          email: localStorage.getItem("email") ?? "",
          id: Number(localStorage.getItem("id")),
          userType: Number(localStorage.getItem("userType")),
          quickbooks_connected: localStorage.getItem("quickbooksConnected") === "true",
          tenant: storedTenant(),
        },
      })
    );
  };

export const updateAccountDetails =
  (payload: AccountDetailsPayload) =>
  async (dispatch: Dispatch<AuthAction>): Promise<void> => {
    dispatch(authStart());
    try {
      await axios.post(`${config.url.API_URL}users/${localStorage.getItem("id")}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken") ?? ""}` },
      });
      dispatch(updateAccountSuccess(payload));
    } catch (error: unknown) {
      dispatch(authFail(axios.isAxiosError<ApiErrorPayload>(error) ? error.response : undefined));
    }
  };
